import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { logger } from '@/utils/logger';
import { Gear, Key, SignOut, Eye, EyeSlash, Bell, ArrowClockwise, CloudArrowUp, Database, Sun, Moon, Desktop, Star, Newspaper, FileArrowDown, Target, ClockCounterClockwise, ShareNetwork, ChartLineUp, EnvelopeSimple, PaperPlaneTilt } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { buildPlanTools } from '@/utils/planTools';
import AutoSRHistoryModal from './AutoSRHistoryModal';
import MarketCalendarModal from './MarketCalendarModal';
import { CalendarBlank } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Opciones de tema
const themes = [
  { id: 'light', name: 'Claro', icon: Sun, color: '#00dcff' },
  { id: 'dark', name: 'Oscuro', icon: Moon, color: '#6366f1' },
  { id: 'system', name: 'Sistema', icon: Desktop, color: '#22c55e' }
];

export const Settings = ({ 
  onLogout, 
  onShowAlerts, 
  onManualRefresh, 
  onSoftRefresh, 
  onSaveCloud, 
  onOpenBackup,
  loading = false,
  savingToCloud = false,
  saveSuccess = false,
  alertsCount = 0,
  currentUser = null,
  onOpenAdmin = null,
  isAdmin = false
}) => {
  const [showSettings, setShowSettings] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showMarketCalendar, setShowMarketCalendar] = useState(false);
  // Tick para forzar re-render del segmentado del refresco del gráfico
  // (cuando el usuario cambia opción, actualiza localStorage y necesitamos
  // que el botón activo cambie visualmente).
  const [chartRefreshTick, setChartRefreshTick] = useState(0);

  // Modo Presentación (global): oculta cifras (€•••••) en toda la app y oculta
  // automáticamente los widgets menos relevantes del dashboard. Se sincroniza vía
  // localStorage `kapital_presentation_mode` + evento `presentationModeChanged`.
  const [presentationMode, setPresentationMode] = useState(() => {
    try { return localStorage.getItem('kapital_presentation_mode') === 'true'; } catch { return false; }
  });
  useEffect(() => {
    const onChange = (e) => setPresentationMode(!!e?.detail?.enabled);
    window.addEventListener('presentationModeChanged', onChange);
    return () => window.removeEventListener('presentationModeChanged', onChange);
  }, []);
  const togglePresentationMode = () => {
    const next = !presentationMode;
    setPresentationMode(next);
    try { localStorage.setItem('kapital_presentation_mode', next.toString()); } catch { /* noop */ }
    window.dispatchEvent(new CustomEvent('presentationModeChanged', { detail: { enabled: next } }));
    toast.success(next ? 'Modo Presentación activado' : 'Modo Presentación desactivado', { duration: 1500 });
  };

  // Descarga PDF con comparativa COMPLETA de planes (Gratis / Pro / Ultimate)
  const handleDownloadPlansPDF = () => {
    try {
      const planTools = buildPlanTools();
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();

      // Portada
      doc.setFillColor(10, 22, 40);
      doc.rect(0, 0, pageWidth, 120, 'F');
      doc.setTextColor(255, 214, 0);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(26);
      doc.text('KAPITAL · Herramientas por plan', pageWidth / 2, 55, { align: 'center' });
      doc.setTextColor(180, 200, 220);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text('Comparativa completa · Gratis · Pro (€19.99) · Ultimate (€29.99)', pageWidth / 2, 82, { align: 'center' });
      doc.setFontSize(9);
      doc.text(`Generado el ${new Date().toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })}`, pageWidth / 2, 100, { align: 'center' });

      const plans = [
        { id: 'free', name: 'Gratis', priceLabel: 'Gratis', target: 'Para explorar la plataforma', tools: planTools.free, color: [100, 116, 139] },
        { id: 'pro', name: 'Pro', priceLabel: '€19.99', target: 'Ideal para principiantes', tools: planTools.pro, color: [6, 182, 212] },
        { id: 'ultimate', name: 'Ultimate', priceLabel: '€29.99', target: 'Para traders con experiencia', tools: planTools.ultimate, color: [245, 158, 11] },
      ];

      autoTable(doc, {
        startY: 140,
        head: [['Plan', 'Precio', 'Total', 'Recomendado para']],
        body: plans.map(p => [p.name, p.priceLabel + ' (pago único)', `${p.tools.length} herramientas`, p.target]),
        theme: 'grid',
        headStyles: { fillColor: [15, 118, 176], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 10, cellPadding: 6 },
        alternateRowStyles: { fillColor: [246, 250, 253] },
      });

      plans.forEach(p => {
        doc.addPage();
        doc.setFillColor(...p.color);
        doc.rect(0, 0, pageWidth, 70, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(22);
        doc.text(`Plan ${p.name} — ${p.priceLabel}`, 40, 35);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.text(`${p.target} · ${p.tools.length} herramientas incluidas`, 40, 55);

        autoTable(doc, {
          startY: 90,
          head: [['#', 'Herramienta', 'Descripción']],
          body: p.tools.map((t, idx) => [String(idx + 1), t.name, t.description || '—']),
          theme: 'grid',
          headStyles: { fillColor: p.color, textColor: 255, fontStyle: 'bold' },
          styles: { fontSize: 9.5, cellPadding: 5 },
          alternateRowStyles: { fillColor: [246, 250, 253] },
          columnStyles: {
            0: { cellWidth: 30, halign: 'center' },
            1: { cellWidth: 180, fontStyle: 'bold' },
          },
        });
      });

      doc.save(`KAPITAL_planes_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('PDF de planes descargado');
    } catch (e) {
      logger.error('Error generando PDF de planes', e);
      toast.error('No se pudo generar el PDF');
    }
  };

  const [pushState, setPushState] = useState(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    return Notification.permission;
  });
  // Toggle "Alerta flash" (≥umbral configurable en 1 min). Default ON.
  const [flashAlertEnabled, setFlashAlertEnabled] = useState(() => {
    try {
      const raw = localStorage.getItem('flash_alert_enabled');
      return raw === null ? true : raw === 'true';
    } catch { return true; }
  });
  // Feb 2026: Umbral configurable de la alerta flash (2% / 3% / 5%). Persiste
  // en localStorage y notifica al detector vía evento `flashAlertThresholdChange`.
  const [flashAlertPct, setFlashAlertPct] = useState(() => {
    try {
      const raw = localStorage.getItem('flash_alert_threshold_pct');
      const n = raw ? parseFloat(raw) : 2.0;
      return [2.0, 3.0, 5.0].includes(n) ? n : 2.0;
    } catch { return 2.0; }
  });
  // Toggle "Push alertas analistas Tier-1". Cargado del backend en useEffect.
  const [analystTier1PushEnabled, setAnalystTier1PushEnabled] = useState(true);
  // Toggle "Alertas Rotura de Nivel" (auto-SR). Cargado del backend en useEffect.
  const [autoSrEnabled, setAutoSrEnabled] = useState(true);
  // Toggle "Informe semanal por email" (opt-in). Cargado del backend en useEffect.
  const [weeklyDigestEnabled, setWeeklyDigestEnabled] = useState(false);
  const [digestSending, setDigestSending] = useState(false);
  // Toggle "Alertas de hueco de apertura" (gap). Default activado. Cargado del backend.
  const [gapAlertsEnabled, setGapAlertsEnabled] = useState(true);
  // Modal historial de rotura de nivel
  const [showAutoSrHistory, setShowAutoSrHistory] = useState(false);
  // Umbral de cambio fuerte de cartera (0.5% / 1% / 2%). Persiste en localStorage.
  const [strongMovePct, setStrongMovePct] = useState(() => {
    try {
      const raw = localStorage.getItem('strong_move_threshold_pct');
      const n = raw ? parseFloat(raw) : 1.0;
      return [0.5, 1.0, 2.0].includes(n) ? n : 1.0;
    } catch { return 1.0; }
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isLoading, setIsLoading] = useState(false);
  
  // Hook del tema
  const { theme, setTheme, accent, setAccent } = useTheme();
  const [previewAccent, setPreviewAccent] = useState(null);
  const currentTheme = themes.find(t => t.id === theme) || themes[0];

  // Cargar preferencia de push alertas de analistas al abrir Settings
  useEffect(() => {
    if (!showSettings) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
        const resp = await axios.get(`${API}/analyst-alerts/settings`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
          timeout: 8000,
        });
        if (!cancelled) {
          setAnalystTier1PushEnabled(!!resp.data?.analyst_tier1_push_enabled);
        }
      } catch { /* mantener default */ }
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
        const resp = await axios.get(`${API}/auto-sr-alerts/settings`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
          timeout: 8000,
        });
        if (!cancelled) {
          setAutoSrEnabled(!!resp.data?.auto_sr_alerts_enabled);
        }
      } catch { /* mantener default */ }
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
        const resp = await axios.get(`${API}/weekly-digest/settings`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
          timeout: 8000,
        });
        if (!cancelled) {
          setWeeklyDigestEnabled(!!resp.data?.weekly_digest_enabled);
        }
      } catch { /* mantener default */ }
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
        const resp = await axios.get(`${API}/gap-alerts/settings`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
          timeout: 8000,
        });
        if (!cancelled) {
          setGapAlertsEnabled(resp.data?.gap_alerts_enabled !== false);
        }
      } catch { /* mantener default */ }
    })();
    return () => { cancelled = true; };
  }, [showSettings]);

  const handleToggleAnalystTier1Push = async () => {
    const next = !analystTier1PushEnabled;
    setAnalystTier1PushEnabled(next);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      await axios.put(
        `${API}/analyst-alerts/settings`,
        { analyst_tier1_push_enabled: next },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
          timeout: 8000,
        }
      );
      toast.success(`Alertas push de analistas Tier-1 ${next ? 'activadas' : 'desactivadas'}`);
    } catch {
      // Revertir en caso de error
      setAnalystTier1PushEnabled(!next);
      toast.error('No se pudo guardar la preferencia');
    }
  };

  const handleToggleAutoSr = async () => {
    const next = !autoSrEnabled;
    setAutoSrEnabled(next);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      await axios.put(
        `${API}/auto-sr-alerts/settings`,
        { auto_sr_alerts_enabled: next },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
          timeout: 8000,
        }
      );
      toast.success(`Alertas de rotura de nivel ${next ? 'activadas' : 'desactivadas'}`);
    } catch {
      setAutoSrEnabled(!next);
      toast.error('No se pudo guardar la preferencia');
    }
  };

  const handleToggleWeeklyDigest = async () => {
    const next = !weeklyDigestEnabled;
    setWeeklyDigestEnabled(next);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      await axios.put(
        `${API}/weekly-digest/settings`,
        { weekly_digest_enabled: next },
        { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true, timeout: 8000 }
      );
      toast.success(`Informe semanal ${next ? 'activado' : 'desactivado'}`);
    } catch {
      setWeeklyDigestEnabled(!next);
      toast.error('No se pudo guardar la preferencia');
    }
  };

  const handleSendDigestNow = async () => {
    setDigestSending(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const resp = await axios.post(
        `${API}/weekly-digest/send-now`, {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true, timeout: 30000 }
      );
      toast.success(`Informe enviado a ${resp.data?.email || 'tu email'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar el informe');
    } finally {
      setDigestSending(false);
    }
  };

  const handleToggleGapAlerts = async () => {
    const next = !gapAlertsEnabled;
    setGapAlertsEnabled(next);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      await axios.put(
        `${API}/gap-alerts/settings`,
        { gap_alerts_enabled: next },
        { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true, timeout: 8000 }
      );
      toast.success(`Alertas de hueco de apertura ${next ? 'activadas' : 'desactivadas'}`);
    } catch {
      setGapAlertsEnabled(!next);
      toast.error('No se pudo guardar la preferencia');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }

    setIsLoading(true);
    try {
      // Using withCredentials for httpOnly cookie auth (no manual token needed)
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        },
        { withCredentials: true }
      );
      toast.success('Contraseña actualizada exitosamente');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setShowChangePassword(false);
    } catch (error) {
      logger.error('Error cambiando contraseña:', error);
      if (error.response?.status === 401) {
        toast.error('Contraseña actual incorrecta');
      } else {
        toast.error('Error al cambiar contraseña');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      {/* Botón de configuración */}
      <button
        onClick={() => setShowSettings(!showSettings)}
        className="p-2 bg-zinc-900 border border-gold-500/60 rounded-sm hover:bg-zinc-800 hover:border-gold-400 transition-colors shadow-[0_0_10px_rgba(251,191,36,0.15)]"
        title="Configuración"
        data-testid="settings-button"
      >
        <Gear size={20} className="text-gold-400" />
      </button>

      {/* Menú desplegable - usando Portal para evitar problemas de z-index */}
      {showSettings && createPortal(
        <>
          <div 
            className="fixed inset-0 z-[9998]" 
            onClick={() => setShowSettings(false)}
          />
          <div className="fixed top-14 right-4 w-72 max-h-[calc(100vh-80px)] overflow-y-auto flex flex-col bg-zinc-900/95 backdrop-blur-sm border border-gold-500/30 rounded-xl shadow-2xl z-[9999]"
            style={{ boxShadow: '0 0 40px rgba(0,220,255,0.15), 0 20px 50px rgba(0,0,0,0.9)' }}>
            <div className="p-4 border-b border-zinc-700/50 order-0">
              <div className="text-sm font-bold text-white">Configuración</div>
              <div className="text-xs text-zinc-400 mt-1">Herramientas y ajustes</div>
            </div>
            
            {/* Sección: Acciones Rápidas */}
            <div className="p-3 border-b border-zinc-700/30 order-3 space-y-3">
              {/* Subgrupo: Herramientas */}
              <div>
                <div className="flex items-center gap-2 px-1 pb-1.5">
                  <span className="w-1 h-3 rounded-full bg-gold-400/80" />
                  <span className="text-[11px] font-bold text-gold-400/80 uppercase tracking-widest">Herramientas</span>
                </div>
                <div className="space-y-0.5">
                  {onShowAlerts && (
                    <button
                      onClick={() => { onShowAlerts(); setShowSettings(false); }}
                      className="group relative w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/[0.04] transition-colors"
                    >
                      <span className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-white/[0.04] border border-white/10 group-hover:border-gold-500/40 transition-colors">
                        <Bell size={17} className="text-gold-400" />
                      </span>
                      <span className="flex-1 text-left">Alertas</span>
                      {alertsCount > 0 && (
                        <span className="min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                          {alertsCount > 9 ? '9+' : alertsCount}
                        </span>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => { window.dispatchEvent(new Event('openMyShares')); setShowSettings(false); }}
                    className="group w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/[0.04] transition-colors"
                    data-testid="settings-my-shares"
                  >
                    <span className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-white/[0.04] border border-white/10 group-hover:border-gold-500/40 transition-colors">
                      <ShareNetwork size={17} className="text-sky-400" />
                    </span>
                    <span className="flex-1 text-left">Mis Compartidos</span>
                  </button>
                  <button
                    onClick={() => { setShowMarketCalendar(true); setShowSettings(false); }}
                    className="group w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/[0.04] transition-colors"
                    data-testid="settings-market-calendar"
                  >
                    <span className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-white/[0.04] border border-white/10 group-hover:border-gold-500/40 transition-colors">
                      <CalendarBlank size={17} className="text-gold-300" />
                    </span>
                    <span className="flex-1 text-left">Calendario de Mercados</span>
                  </button>
                </div>
              </div>

              {/* Subgrupo: Datos y copias */}
              <div>
                <div className="flex items-center gap-2 px-1 pb-1.5">
                  <span className="w-1 h-3 rounded-full bg-gold-400/80" />
                  <span className="text-[11px] font-bold text-gold-400/80 uppercase tracking-widest">Datos y copias</span>
                </div>
                <div className="space-y-0.5">
                  {onSaveCloud && (
                    <button
                      onClick={() => { onSaveCloud(); }}
                      disabled={savingToCloud}
                      className="group w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/[0.04] transition-colors disabled:opacity-50"
                    >
                      <span className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center border transition-colors ${saveSuccess ? 'bg-green-500/15 border-green-500/40' : 'bg-white/[0.04] border-white/10 group-hover:border-gold-500/40'}`}>
                        <CloudArrowUp size={17} className="text-green-400" />
                      </span>
                      <span className="flex-1 text-left">{savingToCloud ? 'Guardando...' : saveSuccess ? '¡Guardado!' : 'Guardar en Nube'}</span>
                    </button>
                  )}
                  {onOpenBackup && (
                    <button
                      onClick={() => { onOpenBackup(); setShowSettings(false); }}
                      className="group w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/[0.04] transition-colors"
                    >
                      <span className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-white/[0.04] border border-white/10 group-hover:border-gold-500/40 transition-colors">
                        <Database size={17} className="text-emerald-400" />
                      </span>
                      <span className="flex-1 text-left">Backup</span>
                    </button>
                  )}
                  <button
                    onClick={() => { handleDownloadPlansPDF(); setShowSettings(false); }}
                    className="group w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/[0.04] transition-colors"
                    data-testid="settings-download-plans-pdf"
                    title="Descarga un PDF con la comparativa completa de los 3 planes y todas sus herramientas"
                  >
                    <span className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-white/[0.04] border border-white/10 group-hover:border-gold-500/40 transition-colors">
                      <FileArrowDown size={17} className="text-gold-400" />
                    </span>
                    <span className="flex-1 text-left">Descargar planes (PDF)</span>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Sección: Notificaciones */}
            <div className="p-2 border-b border-zinc-700/30 order-4">
              <div className="flex items-center gap-2 px-1 pt-0.5 pb-1.5">
                <span className="w-1 h-3 rounded-full bg-gold-400/80" />
                <span className="text-[11px] font-bold text-gold-400/80 uppercase tracking-widest">Notificaciones</span>
              </div>
              <div className="px-2 py-2">
                <div className="text-xs text-zinc-400 mb-2 flex items-center gap-1.5">
                  <Bell size={12} className="text-gold-400" />
                  Cambio fuerte de cartera
                </div>
                <div className="flex gap-1.5" data-testid="strong-move-threshold-selector">
                  {[0.5, 1.0, 2.0].map((pct) => {
                    const isActive = strongMovePct === pct;
                    return (
                      <button
                        key={pct}
                        onClick={() => {
                          setStrongMovePct(pct);
                          try {
                            localStorage.setItem('strong_move_threshold_pct', String(pct));
                            window.dispatchEvent(new CustomEvent('strongMoveThresholdChanged', { detail: { pct } }));
                          } catch { /* ignore */ }
                          toast.success(`Umbral fijado en ${pct}%`);
                        }}
                        className={`flex-1 px-2 py-2 rounded-lg text-xs font-bold font-mono transition-all ${
                          isActive
                            ? 'bg-gold-500/25 border border-gold-500/60 text-gold-300'
                            : 'bg-zinc-800/60 border border-zinc-700 text-zinc-400 hover:border-zinc-500'
                        }`}
                        data-testid={`strong-move-threshold-${pct}`}
                      >
                        {pct}%
                      </button>
                    );
                  })}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1.5 leading-snug">
                  Aviso toast cuando la cartera se mueva ≥{strongMovePct}% en ≤5 min. Cooldown 10 min por dirección.
                </div>

                {/* Toggle: alerta flash + selector umbral (2/3/5%) en 1 min */}
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <label className="flex items-center gap-2.5 cursor-pointer group">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={flashAlertEnabled}
                      onClick={() => {
                        const next = !flashAlertEnabled;
                        setFlashAlertEnabled(next);
                        try {
                          localStorage.setItem('flash_alert_enabled', String(next));
                          window.dispatchEvent(new CustomEvent('flashAlertToggle', { detail: { enabled: next } }));
                        } catch { /* ignore */ }
                        toast.success(`Alerta flash ${next ? 'activada' : 'desactivada'}`);
                      }}
                      className={`relative shrink-0 w-9 h-5 rounded-full border transition-colors ${
                        flashAlertEnabled
                          ? 'bg-gold-500/40 border-gold-500/70'
                          : 'bg-zinc-800 border-zinc-700'
                      }`}
                      data-testid="flash-alert-toggle"
                    >
                      <span
                        className={`absolute top-[2px] w-3.5 h-3.5 rounded-full bg-white shadow transition-all ${
                          flashAlertEnabled ? 'left-[18px]' : 'left-[2px]'
                        }`}
                      />
                    </button>
                    <div className="flex flex-col">
                      <span className="text-xs font-semibold text-zinc-300 flex items-center gap-1.5">
                        <span className="text-gold-400">⚡</span> Alerta flash
                      </span>
                      <span className="text-[10px] text-zinc-500 leading-snug">
                        Aviso extra si sube o baja ≥{flashAlertPct}% en menos de 1 min. Cooldown 5 min.
                      </span>
                    </div>
                  </label>

                  {/* Feb 2026: Selector umbral (solo visible si Flash activo) */}
                  {flashAlertEnabled && (
                    <div className="mt-2.5 ml-11 flex items-center gap-2" data-testid="flash-alert-threshold-group">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Umbral</span>
                      <div className="flex gap-1">
                        {[2.0, 3.0, 5.0].map(p => {
                          const active = flashAlertPct === p;
                          return (
                            <button
                              key={p}
                              type="button"
                              data-testid={`flash-alert-pct-${p.toFixed(1)}`}
                              onClick={() => {
                                setFlashAlertPct(p);
                                try {
                                  localStorage.setItem('flash_alert_threshold_pct', String(p));
                                  window.dispatchEvent(new CustomEvent('flashAlertThresholdChange', { detail: { pct: p } }));
                                } catch { /* ignore */ }
                                toast.success(`Umbral flash: ≥${p}% en 1 min`);
                              }}
                              className={`text-[11px] font-mono font-bold px-2.5 py-1 rounded-md border transition-colors ${
                                active
                                  ? 'bg-gold-500/20 border-gold-400/70 text-gold-200 shadow-[0_0_8px_rgba(251,191,36,0.35)]'
                                  : 'bg-zinc-900/60 border-zinc-700 text-zinc-400 hover:border-gold-500/40 hover:text-gold-300'
                              }`}
                            >
                              {p}%
                            </button>
                          );
                        })}
                      </div>
                      <span className="text-[9px] text-zinc-600 leading-tight ml-1">
                        {flashAlertPct === 5.0 ? 'solo movimientos muy fuertes' : flashAlertPct === 3.0 ? 'equilibrio recomendado' : 'más sensible (más avisos)'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Push nativo (PWA): permiso del sistema para notificación real */}
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <div className="text-xs text-zinc-400 mb-2 flex items-center gap-1.5">
                    <Bell size={12} className="text-gold-400" />
                    Notificaciones push del sistema
                  </div>
                  {pushState === 'unsupported' && (
                    <div className="text-[10px] text-zinc-500 leading-snug">
                      Tu navegador no soporta notificaciones push. Instala la PWA en el móvil para activarlas.
                    </div>
                  )}
                  {pushState === 'denied' && (
                    <div className="text-[10px] text-red-400 leading-snug">
                      Permiso denegado. Habilítalas desde los ajustes del navegador para que suenen en el móvil.
                    </div>
                  )}
                  {pushState === 'granted' && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] px-2 py-1 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-semibold">Activadas</span>
                      <button
                        onClick={() => {
                          if ('serviceWorker' in navigator) {
                            navigator.serviceWorker.ready.then((reg) => {
                              reg.showNotification('KAPITAL · Prueba', {
                                body: 'Notificaciones push funcionando correctamente.',
                                icon: '/logo192.png',
                                badge: '/logo192.png',
                                tag: 'kapital-test',
                                vibrate: [100, 50, 100],
                              });
                            });
                          }
                        }}
                        className="text-[10px] px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700"
                        data-testid="push-test-btn"
                      >
                        Enviar test
                      </button>
                    </div>
                  )}
                  {pushState === 'default' && (
                    <button
                      onClick={() => {
                        if ('Notification' in window) {
                          Notification.requestPermission().then((perm) => {
                            setPushState(perm);
                            if (perm === 'granted') {
                              toast.success('Notificaciones push activadas');
                              if ('serviceWorker' in navigator) {
                                navigator.serviceWorker.ready.then((reg) => {
                                  reg.showNotification('KAPITAL', {
                                    body: 'A partir de ahora te avisaré también con notificación real cuando la cartera se mueva fuerte.',
                                    icon: '/logo192.png',
                                    badge: '/logo192.png',
                                    tag: 'kapital-onboarding',
                                    vibrate: [100, 50, 100],
                                  });
                                });
                              }
                            } else if (perm === 'denied') {
                              toast.error('Permiso denegado. Habilítalo desde los ajustes del navegador.');
                            }
                          });
                        }
                      }}
                      className="w-full text-xs font-bold px-3 py-2 rounded-lg bg-gradient-to-r from-gold-600 to-zinc-600 text-white hover:from-gold-500 hover:to-zinc-500 transition-all"
                      data-testid="push-enable-btn"
                    >
                      Activar push móvil
                    </button>
                  )}
                  <div className="text-[10px] text-zinc-600 mt-1.5 leading-snug">
                    Suena/vibra en el móvil aunque la app esté en segundo plano.
                  </div>
                </div>

                {/* Toggle: Push alertas de analistas Tier-1 */}
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <label className="flex items-start gap-2.5 cursor-pointer group">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={analystTier1PushEnabled}
                      onClick={handleToggleAnalystTier1Push}
                      className={`mt-0.5 relative shrink-0 w-9 h-5 rounded-full border transition-colors ${
                        analystTier1PushEnabled
                          ? 'bg-gold-500/40 border-gold-500/70'
                          : 'bg-zinc-800 border-zinc-700'
                      }`}
                      data-testid="analyst-tier1-push-toggle"
                    >
                      <span
                        className={`absolute top-[2px] w-3.5 h-3.5 rounded-full bg-white shadow transition-all ${
                          analystTier1PushEnabled ? 'left-[18px]' : 'left-[2px]'
                        }`}
                      />
                    </button>
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs font-semibold text-zinc-300 flex items-center gap-1.5">
                        <Star size={12} weight="fill" className="text-gold-400" />
                        Analistas Tier-1
                      </span>
                      <span className="text-[10px] text-zinc-500 leading-snug">
                        Push cuando JP Morgan, Goldman, Morgan Stanley u otras firmas
                        top hagan upgrade/downgrade en tu cartera. Las alertas in-app se
                        crean siempre, esto solo controla la notificación push.
                      </span>
                    </div>
                  </label>
                </div>

                {/* Toggle: Alertas de rotura de nivel (auto-SR) */}
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <label className="flex items-start gap-2.5 cursor-pointer group">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={autoSrEnabled}
                      onClick={handleToggleAutoSr}
                      className={`mt-0.5 relative shrink-0 w-9 h-5 rounded-full border transition-colors ${
                        autoSrEnabled
                          ? 'bg-gold-500/40 border-gold-500/70'
                          : 'bg-zinc-800 border-zinc-700'
                      }`}
                      data-testid="auto-sr-toggle"
                    >
                      <span
                        className={`absolute top-[2px] w-3.5 h-3.5 rounded-full bg-white shadow transition-all ${
                          autoSrEnabled ? 'left-[18px]' : 'left-[2px]'
                        }`}
                      />
                    </button>
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-xs font-semibold text-zinc-300 flex items-center gap-1.5">
                        <Target size={12} weight="fill" className="text-gold-400" />
                        Rotura de Soporte / Resistencia
                      </span>
                      <span className="text-[10px] text-zinc-500 leading-snug">
                        Avisa cuando el precio de un activo de tu cartera cruce un
                        soporte o resistencia auto-detectado por análisis histórico
                        (yfinance). Vigilancia cada 2 min con email + evento in-app.
                      </span>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowAutoSrHistory(true); }}
                        className="mt-1.5 self-start flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-gold-300 hover:text-gold-100 bg-gold-500/10 hover:bg-gold-500/20 border border-gold-500/30 hover:border-gold-400/60 transition-all"
                        data-testid="auto-sr-history-open"
                      >
                        <ClockCounterClockwise size={11} weight="bold" />
                        Ver historial
                      </button>
                    </div>
                  </label>
                </div>

                {/* Toggle: Informe semanal por email */}
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <label className="flex items-start gap-2.5 cursor-pointer group">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={weeklyDigestEnabled}
                      onClick={handleToggleWeeklyDigest}
                      className={`mt-0.5 relative shrink-0 w-9 h-5 rounded-full border transition-colors ${
                        weeklyDigestEnabled
                          ? 'bg-gold-500/40 border-gold-500/70'
                          : 'bg-zinc-800 border-zinc-700'
                      }`}
                      data-testid="weekly-digest-toggle"
                    >
                      <span
                        className={`absolute top-[2px] w-3.5 h-3.5 rounded-full bg-white shadow transition-all ${
                          weeklyDigestEnabled ? 'left-[18px]' : 'left-[2px]'
                        }`}
                      />
                    </button>
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-xs font-semibold text-zinc-300 flex items-center gap-1.5">
                        <EnvelopeSimple size={12} weight="fill" className="text-gold-400" />
                        Informe semanal
                      </span>
                      <span className="text-[10px] text-zinc-500 leading-snug">
                        Cada domingo recibes por email un resumen de tu cartera: valor
                        total, variación de la semana, mejores/peores posiciones y
                        movimientos de la Leyenda.
                      </span>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSendDigestNow(); }}
                        disabled={digestSending}
                        className="mt-1.5 self-start flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-gold-300 hover:text-gold-100 bg-gold-500/10 hover:bg-gold-500/20 border border-gold-500/30 hover:border-gold-400/60 transition-all disabled:opacity-50"
                        data-testid="weekly-digest-send-now"
                      >
                        <PaperPlaneTilt size={11} weight="bold" />
                        {digestSending ? 'Enviando…' : 'Enviar ahora'}
                      </button>
                    </div>
                  </label>
                </div>

                {/* Toggle: Alertas de hueco de apertura (gap) */}
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <label className="flex items-start gap-2.5 cursor-pointer group">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={gapAlertsEnabled}
                      onClick={handleToggleGapAlerts}
                      className={`mt-0.5 relative shrink-0 w-9 h-5 rounded-full border transition-colors ${
                        gapAlertsEnabled
                          ? 'bg-gold-500/40 border-gold-500/70'
                          : 'bg-zinc-800 border-zinc-700'
                      }`}
                      data-testid="gap-alerts-toggle"
                    >
                      <span
                        className={`absolute top-[2px] w-3.5 h-3.5 rounded-full bg-white shadow transition-all ${
                          gapAlertsEnabled ? 'left-[18px]' : 'left-[2px]'
                        }`}
                      />
                    </button>
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-xs font-semibold text-zinc-300 flex items-center gap-1.5">
                        <ChartLineUp size={12} weight="fill" className="text-gold-400" />
                        Hueco de apertura (gap)
                      </span>
                      <span className="text-[10px] text-zinc-500 leading-snug">
                        Avisa cuando un activo de tu cartera ABRE con un hueco fuerte
                        (≥3%) respecto al cierre anterior. Se comprueba tras la apertura
                        de Europa (09:35) y EE.UU. (15:35) con alerta in-app + push.
                      </span>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Sección: Modo Presentación */}
            <div className="p-2 border-b border-zinc-700/30 order-4">
              <div className="flex items-center gap-2 px-1 pt-0.5 pb-1.5">
                <span className="w-1 h-3 rounded-full bg-pink-400/80" />
                <span className="text-[11px] font-bold text-pink-300/90 uppercase tracking-widest">Modo Presentación</span>
              </div>
              <div className="px-2 py-1.5">
                <button
                  type="button"
                  onClick={togglePresentationMode}
                  data-testid="settings-presentation-toggle"
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${
                    presentationMode
                      ? 'bg-pink-500/15 border-pink-500/50 text-pink-200'
                      : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                  }`}
                >
                  {presentationMode ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
                  <div className="flex-1 text-left">
                    <div className="text-sm font-semibold">{presentationMode ? 'Activado' : 'Desactivado'}</div>
                    <div className="text-[11px] text-zinc-400 leading-snug">Oculta importes con €••••• y los widgets menos relevantes para compartir pantalla</div>
                  </div>
                  <span className={`shrink-0 w-9 h-5 rounded-full relative transition-colors ${presentationMode ? 'bg-pink-500' : 'bg-zinc-600'}`}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${presentationMode ? 'left-4' : 'left-0.5'}`} />
                  </span>
                </button>
              </div>
            </div>

            {/* Sección: Apariencia */}
            <div className="p-2 border-b border-zinc-700/30 order-5">
              <div className="flex items-center gap-2 px-1 pt-0.5 pb-1.5">
                <span className="w-1 h-3 rounded-full bg-gold-400/80" />
                <span className="text-[11px] font-bold text-gold-400/80 uppercase tracking-widest">Apariencia</span>
              </div>
              <div className="px-2 py-2">
                <div className="text-xs text-zinc-400 mb-2">Tema de la aplicación</div>
                <div className="flex gap-2">
                  {themes.map((t) => {
                    const ThemeIcon = t.icon;
                    const isActive = theme === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setTheme(t.id)}
                        className={`flex-1 flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all ${
                          isActive 
                            ? 'bg-gold-500/20 border border-gold-500/50' 
                            : 'bg-zinc-800/50 border border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <ThemeIcon size={18} style={{ color: isActive ? t.color : '#9ca3af' }} />
                        <span className={`text-xs ${isActive ? 'text-gold-300' : 'text-zinc-400'}`}>{t.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Acento de marca: Oro / Plata */}
              <div className="px-2 py-2 border-t border-zinc-700/30 mt-1">
                <div className="text-xs text-zinc-400 mb-2">Acento de marca</div>
                <div className="flex gap-2" data-testid="accent-selector">
                  {[
                    { id: 'gold', name: 'Oro', swatch: 'linear-gradient(135deg, #edd074, #e8c55c, #bb8a28)' },
                    { id: 'silver', name: 'Plata', swatch: 'linear-gradient(135deg, #e4e4e7, #c7ccd6, #71717a)' },
                    { id: 'copper', name: 'Cobre', swatch: 'linear-gradient(135deg, #d0916b, #b87333, #7a4a20)' },
                    { id: 'emerald', name: 'Esmeralda', swatch: 'linear-gradient(135deg, #57c39c, #2fa87f, #135a44)' },
                  ].map((a) => {
                    const isActive = (accent || 'gold') === a.id;
                    return (
                      <button
                        key={a.id}
                        onClick={() => setAccent(a.id)}
                        onMouseEnter={() => setPreviewAccent(a.id)}
                        onMouseLeave={() => setPreviewAccent(null)}
                        data-testid={`accent-option-${a.id}`}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all ${
                          isActive
                            ? 'bg-gold-500/20 border border-gold-500/50'
                            : 'bg-zinc-800/50 border border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <span
                          className="w-4 h-4 rounded-full border border-white/30 shadow"
                          style={{ background: a.swatch }}
                        />
                        <span className={`text-xs ${isActive ? 'text-gold-300' : 'text-zinc-400'}`}>{a.name}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Vista previa del acento (refleja hover; clic para aplicar) */}
                {(() => {
                  const shown = previewAccent || accent || 'gold';
                  const PAL = {
                    gold: { primary: '#d9ad3a', text: '#e8c55c', border: 'rgba(232,197,92,0.55)', soft: 'rgba(232,197,92,0.14)', name: 'Oro', btnText: '#1a1206' },
                    silver: { primary: '#a1a1aa', text: '#c7ccd6', border: 'rgba(199,204,214,0.55)', soft: 'rgba(199,204,214,0.14)', name: 'Plata', btnText: '#18181b' },
                    copper: { primary: '#b87333', text: '#d0916b', border: 'rgba(193,117,68,0.55)', soft: 'rgba(193,117,68,0.14)', name: 'Cobre', btnText: '#ffffff' },
                    emerald: { primary: '#1e8a66', text: '#57c39c', border: 'rgba(47,168,127,0.55)', soft: 'rgba(47,168,127,0.14)', name: 'Esmeralda', btnText: '#ffffff' },
                  };
                  const P = PAL[shown] || PAL.gold;
                  const isPreviewing = previewAccent && previewAccent !== (accent || 'gold');
                  return (
                    <div className="mt-3" data-testid="accent-preview">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Vista previa</span>
                        <span className="text-[10px]" style={{ color: P.text }}>
                          {isPreviewing ? `${P.name} (previsualizando)` : P.name}
                        </span>
                      </div>
                      <div
                        className="rounded-xl p-3 bg-zinc-900/70 transition-all"
                        style={{ border: `1px solid ${P.border}`, boxShadow: `0 0 18px ${P.soft}` }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span
                              className="w-6 h-6 rounded-md flex items-center justify-center"
                              style={{ background: P.soft, border: `1px solid ${P.border}` }}
                            >
                              <ChartLineUp size={14} weight="bold" style={{ color: P.primary }} />
                            </span>
                            <div className="leading-tight">
                              <div className="text-xs font-bold" style={{ color: P.text }}>AAPL</div>
                              <div className="text-[10px] text-zinc-400">Apple Inc.</div>
                            </div>
                          </div>
                          <div className="text-right leading-tight">
                            <div className="text-xs font-mono font-bold text-white">280,86 €</div>
                            <div className="text-[10px] font-mono font-bold text-green-400">+2,57%</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{ color: P.text, background: P.soft, border: `1px solid ${P.border}` }}
                          >
                            Analista Pro
                          </span>
                          <button
                            type="button"
                            className="ml-auto text-[10px] font-bold px-2.5 py-1 rounded-md"
                            style={{ background: P.primary, color: P.btnText }}
                          >
                            Botón
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="p-2 border-b border-zinc-700/30 order-6">
              <div className="flex items-center gap-2 px-1 pt-0.5 pb-1.5">
                <span className="w-1 h-3 rounded-full bg-gold-400/80 shrink-0" />
                <span className="text-[11px] font-bold text-gold-400/80 uppercase tracking-widest">
                  Actualización del Mapa de Velas Ampliado
                </span>
              </div>
              <div className="px-4 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowClockwise size={14} className="text-gold-400" />
                  <span className="text-sm text-zinc-300 flex-1">Refrescar cada</span>
                  <span
                    className="text-[10px] text-zinc-500 italic"
                    title="Solo afecta al Mapa de Velas Ampliado. El gráfico principal usa su propio ciclo interno."
                  >
                    (solo Velas Ampliado)
                  </span>
                </div>
                <div
                  key={`chart-refresh-${chartRefreshTick}`}
                  className="grid grid-cols-5 gap-1"
                  role="radiogroup"
                  aria-label="Intervalo de actualización del gráfico de velas"
                >
                  {(() => {
                    let current = 300;
                    try {
                      const saved = parseInt(localStorage.getItem('kapital_chart_refresh_seconds'), 10);
                      if ([60, 180, 300, 600, 900].includes(saved)) current = saved;
                    } catch { /* ignore */ }
                    return [
                      { s: 60,  label: '1 min' },
                      { s: 180, label: '3 min' },
                      { s: 300, label: '5 min' },
                      { s: 600, label: '10 min' },
                      { s: 900, label: '15 min' },
                    ].map((opt) => {
                      const active = current === opt.s;
                      return (
                        <button
                          key={opt.s}
                          type="button"
                          onClick={() => {
                            try {
                              localStorage.setItem('kapital_chart_refresh_seconds', String(opt.s));
                              window.dispatchEvent(new CustomEvent('kapital-chart-refresh-changed', {
                                detail: { seconds: opt.s },
                              }));
                            } catch { /* ignore */ }
                            // Forzar re-render local del segmentado
                            setChartRefreshTick((t) => t + 1);
                          }}
                          className={`px-2 py-1.5 rounded-md text-[11px] font-bold transition-colors border ${
                            active
                              ? 'bg-gold-500/25 text-gold-100 border-gold-500/50 shadow-[0_0_8px_rgba(6,182,212,0.2)]'
                              : 'bg-zinc-800/70 text-zinc-400 border-zinc-700 hover:text-zinc-200 hover:border-zinc-600'
                          }`}
                          role="radio"
                          aria-checked={active}
                          data-testid={`chart-refresh-${opt.s}`}
                        >
                          {opt.label}
                        </button>
                      );
                    });
                  })()}
                </div>
                <div className="text-[10px] text-zinc-500 mt-2">
                  Por defecto: 5 min. Intervalos cortos gastan más batería y ancho de banda.
                </div>
              </div>
            </div>

            {/* Sección: Tu Plan */}
            {currentUser && (
              <div className="p-2 border-b border-zinc-700/30 order-2">
              <div className="flex items-center gap-2 px-1 pt-0.5 pb-1.5">
                <span className="w-1 h-3 rounded-full bg-gold-400/80" />
                <span className="text-[11px] font-bold text-gold-400/80 uppercase tracking-widest">Tu Plan</span>
              </div>
                <div className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      currentUser.plan === 'ultimate' ? 'bg-gold-500/20 border border-gold-500/40' :
                      currentUser.plan === 'pro' ? 'bg-gold-500/20 border border-gold-500/40' : 
                      'bg-zinc-700/50 border border-zinc-600'
                    }`}>
                      {currentUser.plan === 'ultimate' ? '👑' : currentUser.plan === 'pro' ? '⭐' : '🆓'}
                    </div>
                    <div>
                      <div className={`font-bold ${
                        currentUser.plan === 'ultimate' ? 'text-gold-400' :
                        currentUser.plan === 'pro' ? 'text-gold-400' : 'text-zinc-300'
                      }`}>
                        {currentUser.plan === 'ultimate' ? 'Ultimate' : 
                         currentUser.plan === 'pro' ? 'Pro' : 'Gratis'}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {currentUser.plan === 'ultimate' ? 'Acceso completo' : 
                         currentUser.plan === 'pro' ? 'Funciones avanzadas' : 'Plan básico'}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Botón Admin - Solo si es admin */}
                {isAdmin && onOpenAdmin && (
                  <button
                    onClick={() => { onOpenAdmin(); setShowSettings(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-400 hover:bg-zinc-500/20 rounded-lg transition-all"
                  >
                    <span className="text-lg">👑</span>
                    <span>Panel de Administración</span>
                  </button>
                )}
              </div>
            )}
            
            {/* Sección: Cuenta */}
            <div className="p-3 space-y-0.5 border-b border-zinc-700/30 order-1">
              <div className="flex items-center gap-2 px-1 pt-0.5 pb-1.5">
                <span className="w-1 h-3 rounded-full bg-gold-400/80" />
                <span className="text-[11px] font-bold text-gold-400/80 uppercase tracking-widest">Cuenta</span>
              </div>
              {/* Recargar */}
              {onManualRefresh && (
                <button
                  onClick={() => { onManualRefresh(); setShowSettings(false); }}
                  disabled={loading}
                  className="group w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/[0.04] transition-colors disabled:opacity-50"
                >
                  <span className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-white/[0.04] border border-white/10 group-hover:border-gold-500/40 transition-colors">
                    <ArrowClockwise size={17} className={`text-zinc-300 ${loading ? 'animate-spin' : ''}`} />
                  </span>
                  <span className="flex-1 text-left">Recargar</span>
                </button>
              )}
              {/* Sincronizar */}
              {onSoftRefresh && (
                <button
                  onClick={() => { onSoftRefresh(); setShowSettings(false); }}
                  disabled={loading}
                  className="group w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/[0.04] transition-colors disabled:opacity-50"
                >
                  <span className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-white/[0.04] border border-white/10 group-hover:border-gold-500/40 transition-colors">
                    <ArrowClockwise size={17} className={`text-gold-400 ${loading ? 'animate-spin' : ''}`} />
                  </span>
                  <span className="flex-1 text-left">Sincronizar</span>
                </button>
              )}
              {/* Limpiar Caché */}
              <button
                onClick={async () => {
                  localStorage.clear();
                  sessionStorage.clear();
                  if ('serviceWorker' in navigator) {
                    const registrations = await navigator.serviceWorker.getRegistrations();
                    for (const registration of registrations) {
                      await registration.unregister();
                    }
                  }
                  if ('caches' in window) {
                    const cacheNames = await caches.keys();
                    for (const cacheName of cacheNames) {
                      await caches.delete(cacheName);
                    }
                  }
                  toast.success('Caché limpiado. Recargando...');
                  setTimeout(() => window.location.reload(true), 1000);
                }}
                className="group w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/[0.04] transition-colors"
              >
                <span className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-white/[0.04] border border-white/10 group-hover:border-gold-500/40 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" fill="currentColor" viewBox="0 0 256 256" className="text-gold-400">
                    <path d="M224,128a96,96,0,0,1-94.71,96H128A95.38,95.38,0,0,1,62.1,197.8a8,8,0,0,1,11.8-10.81,80,80,0,1,0,1.66-114.31L93.77,90.89a8,8,0,1,1-11.54,11.06l-32-33.49A8,8,0,0,1,50.1,57.34l-.09-.1L82.23,25.56A8,8,0,1,1,93.77,36.62L77.53,53.58A96,96,0,0,1,224,128Z"></path>
                  </svg>
                </span>
                <span className="flex-1 text-left">Limpiar Caché</span>
              </button>
              <button
                onClick={() => {
                  setShowChangePassword(true);
                  setShowSettings(false);
                }}
                className="group w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/[0.04] transition-colors"
              >
                <span className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-white/[0.04] border border-white/10 group-hover:border-gold-500/40 transition-colors">
                  <Key size={17} className="text-gold-400" />
                </span>
                <span className="flex-1 text-left">Cambiar Contraseña</span>
              </button>
              {/* Ver Novedades — enlace directo al documento paso-a-paso */}
              <button
                onClick={() => {
                  window.open('/CAMBIOS_HOY.html', '_blank', 'noopener,noreferrer');
                  setShowSettings(false);
                  try { localStorage.setItem('kapital_novedades_seen', '1'); } catch { /* ignore */ }
                }}
                className="group w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-zinc-200 hover:bg-white/[0.04] transition-colors"
                data-testid="ver-novedades-btn"
                title="Abre el paso-a-paso de las últimas mejoras y bugfixes"
              >
                <span className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-white/[0.04] border border-white/10 group-hover:border-gold-500/40 transition-colors">
                  <Newspaper size={17} weight="duotone" className="text-gold-300" />
                </span>
                <span className="flex-1 text-left">Ver Novedades</span>
                {(() => {
                  let unseen = true;
                  try { unseen = localStorage.getItem('kapital_novedades_seen') !== '1'; } catch { /* ignore */ }
                  return unseen ? (
                    <span
                      className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-gold-500/30 text-gold-100 border border-gold-400/40 animate-pulse"
                    >
                      NUEVO
                    </span>
                  ) : null;
                })()}
              </button>
              <div className="my-1.5 border-t border-white/5" />
              <button
                onClick={() => {
                  onLogout();
                  setShowSettings(false);
                }}
                className="group w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                data-testid="logout-button"
              >
                <span className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-red-500/10 border border-red-500/30 group-hover:border-red-500/50 transition-colors">
                  <SignOut size={17} className="text-red-400" />
                </span>
                <span className="flex-1 text-left">Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Modal de cambio de contraseña */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-white mb-4">Cambiar Contraseña</h3>
            
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Contraseña Actual
                </label>
                <div className="relative">
                  <Input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                    required
                    className="bg-zinc-900 border-zinc-800 text-white pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPasswords.current ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <Input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                    required
                    minLength={6}
                    className="bg-zinc-900 border-zinc-800 text-white pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPasswords.new ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Confirmar Nueva Contraseña
                </label>
                <div className="relative">
                  <Input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                    required
                    minLength={6}
                    className="bg-zinc-900 border-zinc-800 text-white pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPasswords.confirm ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-2 bg-white text-zinc-950 font-semibold rounded-sm hover:bg-zinc-100 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePassword(false);
                    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                  }}
                  className="flex-1 py-2 bg-zinc-800 text-zinc-300 rounded-sm hover:bg-zinc-700 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal historial de rotura de nivel — renderizado en portal */}
      {showAutoSrHistory && createPortal(
        <AutoSRHistoryModal open={showAutoSrHistory} onClose={() => setShowAutoSrHistory(false)} />,
        document.body,
      )}
      <MarketCalendarModal open={showMarketCalendar} onClose={() => setShowMarketCalendar(false)} />
    </div>
  );
};

export default Settings;